import {
  currencyTokenTypeNullableSchema,
  currencyTokenTypeSchema
} from '@apeiron/currency'
import {
  ClaimRewardContractInfo,
  DailyCheckInBonus,
  DailyCheckInCampaign,
  DailyCheckInClaim,
  DailyCheckInDay,
  DailyCheckInReward,
  DailyCheckInRewardEarnType,
  DailyCheckInRewardMeta,
  DailyCheckInRewardStatus,
  DailyCheckInShop,
  DailyCheckInShopItemCost,
  DailyCheckInShopRewardType,
  GiftCodeRewardType,
  Reward,
  RewardBalance,
  RewardClaimHistory,
  RewardEarnHistory,
  RewardErcType,
  RewardType
} from '@apeiron/reward'
import { chainNetworkTypeSchema, stringSchema } from '@apeiron/temp'
import { RewardInfo } from '@src/types/reward'
import * as yup from 'yup'

const rewardTypeSchema: yup.SchemaOf<RewardType> = yup
  .mixed()
  .oneOf(Object.values(RewardType))
  .default(RewardType.Weth)

const rewardErcTypeSchema: yup.SchemaOf<RewardErcType> = yup
  .mixed()
  .oneOf(Object.values(RewardErcType))
  .default(RewardErcType.None)

const rewardSchema: yup.SchemaOf<Reward> = yup
  .object({
    addressKey: yup.string().default(''),
    currencyType: currencyTokenTypeNullableSchema,
    image: yup.string().default(''),
    name: yup.string().default('')
  })
  .from('addressConfig', 'addressKey')
  .from('type', 'currencyType')

const dailyRewardMetaSchema: yup.SchemaOf<DailyCheckInRewardMeta> = yup.object({
  name: yup.string().default(''),
  image: yup.string().default('')
})

const dailyCheckInRewardSchema: yup.SchemaOf<DailyCheckInReward> = yup
  .object({
    meta: dailyRewardMetaSchema.nullable(),
    quantity: yup.number().default(0),
    rewardID: yup.string().default(''),
    rewardType: yup
      .mixed()
      .oneOf(Object.values(GiftCodeRewardType))
      .default(GiftCodeRewardType.ClaimableReward)
  })
  .from('metaKey', 'meta')

const dailyCheckInRewardStatusSchema: yup.SchemaOf<DailyCheckInRewardStatus> =
  yup
    .mixed()
    .oneOf(Object.values(DailyCheckInRewardStatus))
    .default(DailyCheckInRewardStatus.Locked)

const dailyCheckInDaySchema: yup.SchemaOf<DailyCheckInDay> = yup.object({
  cycle: yup.number().default(0),
  date: yup.string().default(''),
  day: yup.number().default(0),
  isCurrent: yup.boolean().default(false),
  isPast: yup.boolean().default(false),
  status: dailyCheckInRewardStatusSchema,
  earnType: yup
    .mixed()
    .oneOf(Object.values(DailyCheckInRewardEarnType))
    .nullable(),
  rewards: yup.array().of(dailyCheckInRewardSchema)
})

const dailyCheckInBonusSchema: yup.SchemaOf<DailyCheckInBonus> = yup
  .object({
    bonusHash: yup.string().default(''),
    meta: yup.object({
      description: yup.string().default(''),
      index: yup.number().default(0)
    }),
    rewards: yup.array().of(dailyCheckInRewardSchema),
    status: dailyCheckInRewardStatusSchema
  })
  .from('metaKey', 'meta')

const dailyCheckInShopItemCost: yup.SchemaOf<DailyCheckInShopItemCost> = yup
  .object({
    currencyCode: currencyTokenTypeSchema,
    meta: dailyRewardMetaSchema,
    quantity: yup.string().default('0')
  })
  .from('UserAssetName', 'currencyCode')
  .from('metaKey', 'meta')

const dailyCheckInShopRewardTypeSchema: yup.SchemaOf<DailyCheckInShopRewardType> =
  yup
    .mixed()
    .oneOf(Object.values(DailyCheckInShopRewardType))
    .default(DailyCheckInShopRewardType.Ron)

export const rewardBalanceSchema: yup.SchemaOf<RewardBalance> = yup
  .object({
    assetName: yup.string().default(''),
    decimal: yup.number().default(0),
    ercType: rewardErcTypeSchema,
    id: yup.string().default(''),
    income: yup.string().default('0'),
    pending: yup.string().default('0'),
    reward: rewardSchema,
    totalClaimed: yup.string().default('0')
  })
  .from('rewardInfo.decimal', 'decimal')
  .from('rewardInfo.rewardName', 'assetName')
  .from('rewardInfo.rewardTokenID', 'id')
  .from('rewardInfo.staticAssetKey.reward', 'reward')
  .from('rewardInfo.tokenErcType', 'ercType')
  .noUnknown(true)

export const rewardClaimHistorySchema: yup.SchemaOf<RewardClaimHistory> = yup
  .object({
    claimed: yup.string().default(''),
    claimedAt: yup.string().default(''),
    network: chainNetworkTypeSchema,
    reward: rewardSchema,
    transactionHash: yup.string().default('')
  })
  .from('claimedValue', 'claimed')
  .from('rewardInfo.staticAssetKey.reward', 'reward')
  .from('txnHash', 'transactionHash')
  .from('txnTime', 'claimedAt')
  .noUnknown(true)

export const claimRewardContractSchema: yup.SchemaOf<ClaimRewardContractInfo> =
  yup.object({
    walletAddress: stringSchema(''),
    tokenErcType: yup.array().of(yup.number()).default([]),
    rewardTokenAddress: yup.array().of(yup.string()).default([]),
    rewardTokenID: yup.array().of(yup.string()).default([]),
    totalAmount: yup.array().of(yup.string()).default([]),
    timeStampInSec: yup.number().default(0),
    signature: stringSchema('')
  })

export const dailyCheckInCampaignSchema: yup.SchemaOf<DailyCheckInCampaign> =
  yup.object({
    bonuses: yup.array().of(dailyCheckInBonusSchema),
    campaignID: yup.string().default(''),
    days: yup.array().of(dailyCheckInDaySchema)
  })

export const dailyCheckInShopSchema: yup.SchemaOf<DailyCheckInShop> = yup
  .object({
    displayOrder: yup.number().default(0),
    itemCost: dailyCheckInShopItemCost,
    remainCount: yup.number().default(0),
    shopItemId: yup.string().default(''),
    totalCount: yup.number().default(0),
    shopRewards: yup.array().of(
      dailyCheckInRewardSchema.concat(
        yup.object({
          itemType: dailyCheckInShopRewardTypeSchema
        })
      )
    )
  })
  .from('shopItemID', 'shopItemId')

export const dailyCheckInClaimSchema: yup.SchemaOf<DailyCheckInClaim> =
  yup.object({
    bonusHash: yup.string().default('').nullable(),
    campaignID: yup.string().default(''),
    cycle: yup.number().default(0),
    day: yup.number().default(0).nullable(),
    isClaimedSuccess: yup.boolean().default(false)
  })

export const rewardEarnHistorySchema: yup.SchemaOf<RewardEarnHistory> = yup
  .object({
    network: chainNetworkTypeSchema,
    rewardTime: yup.string().default(''),
    rewardAmount: yup.string().default(''),
    rewardType: rewardTypeSchema,
    settled: yup.boolean().default(false),
    settleTime: yup.string().default(''),
    action: yup.mixed().transform(value => value?.name ?? '-'),
    actionEnum: stringSchema('')
  })
  .from('actionEnumMetaKey', 'action')

export const rewardTokenSchema: yup.SchemaOf<RewardInfo> = yup
  .object({
    address: yup.string().default('0x0'),
    amount: yup.string().nullable().default('0'),
    assetName: yup.string().default(''),
    decimals: yup.number().default(0),
    image: yup.string().default(''),
    imageLite: yup.string().default(''),
    name: yup.string().default(''),
    type: currencyTokenTypeSchema
  })
  .from('rewardTokenAddress', 'address')

export const rewardInfoSchema: yup.SchemaOf<RewardInfo> = rewardTokenSchema
  .from('decimal', 'decimals')
  .from('rewardName', 'assetName')
  .from('staticAssetKey.reward.image', 'image')
  .from('staticAssetKey.reward.imageLite', 'imageLite')
  .from('staticAssetKey.reward.name', 'name')
  .from('staticAssetKey.reward.type', 'type')
  .noUnknown(true)
